import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarPlus, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function CopyDayDialog({ fromDate, groupId, groups = [], onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [toDate, setToDate] = useState(() => {
    const d = new Date(fromDate); d.setDate(d.getDate() + 1); return d.toISOString().slice(0,10);
  });
  const [gid, setGid] = useState(groupId || "");
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [mode, setMode] = useState("add");

  const reset = () => { setConflicts(null); setMode("add"); };

  const run = async () => {
    setLoading(true);
    try {
      const preview = await api.post("/trainings/duplicate-day", {
        from_date: fromDate, to_date: toDate, group_id: gid || null, preview: true,
      });
      if (preview.data.source_count === 0) {
        toast.error("Nessun allenamento nel giorno sorgente");
        setLoading(false); return;
      }
      if (preview.data.target_existing > 0 && !conflicts) {
        setConflicts(preview.data); setLoading(false); return;
      }
      const { data } = await api.post("/trainings/duplicate-day", {
        from_date: fromDate, to_date: toDate, group_id: gid || null, replace: mode === "replace",
      });
      const parts = [`${data.copied} allenamenti copiati in ${toDate}`];
      if (data.target_replaced) parts.push(`(${data.target_replaced} sostituiti)`);
      toast.success(parts.join(" "));
      setOpen(false); reset(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copia giorno di allenamenti</DialogTitle>
          <DialogDescription>Duplica le sessioni del <span className="font-mono">{fromDate}</span> verso un altro giorno.</DialogDescription>
        </DialogHeader>

        {!conflicts && (
          <div className="space-y-3 py-2">
            <div>
              <Label>Giorno di destinazione</Label>
              <Input data-testid="copyday-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div>
              <Label>Filtra per gruppo (opzionale)</Label>
              <Select value={gid || "all"} onValueChange={(v) => setGid(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="copyday-group"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i gruppi</SelectItem>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {conflicts && (
          <div className="space-y-3 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-amber-900">Conflitto</div>
                <div className="text-amber-800 mt-1">Nel giorno destinazione esistono già <strong className="font-mono">{conflicts.target_existing}</strong> allenamento/i.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="copyday-mode-add" onClick={() => setMode("add")} className={`p-3 rounded-md border text-sm text-left ${mode === "add" ? "border-black bg-zinc-50" : "border-zinc-200"}`}>
                <div className="font-semibold">Aggiungi</div>
                <div className="text-xs text-zinc-500 mt-1">Mantieni esistenti + copia {conflicts.source_count}</div>
              </button>
              <button data-testid="copyday-mode-replace" onClick={() => setMode("replace")} className={`p-3 rounded-md border text-sm text-left ${mode === "replace" ? "border-red-600 bg-red-50" : "border-zinc-200"}`}>
                <div className="font-semibold text-red-700">Sostituisci</div>
                <div className="text-xs text-zinc-500 mt-1">Elimina {conflicts.target_existing} esistenti</div>
              </button>
            </div>
          </div>
        )}

        <DialogFooter>
          {!conflicts ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button data-testid="copyday-run" onClick={run} disabled={loading} className="bg-black hover:bg-zinc-800">{loading ? "Verifica…" : "Copia"}</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setConflicts(null)}>Indietro</Button>
              <Button data-testid="copyday-confirm" onClick={run} disabled={loading} className={mode === "replace" ? "bg-red-600 hover:bg-red-700" : "bg-black hover:bg-zinc-800"}>
                {loading ? "Copia…" : (mode === "replace" ? "Sostituisci e copia" : "Aggiungi copie")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
